package com.anjuros.website.redesign.sections.contactforms

import androidx.compose.runtime.Composable
import com.anjuros.website.components.language.LocalText
import com.anjuros.website.redesign.styles.FormsCardStyle
import com.anjuros.website.redesign.styles.FormsCardTitleStyle
import com.anjuros.website.redesign.styles.FormsChoiceCheckedStyle
import com.anjuros.website.redesign.styles.FormsChoicesStyle
import com.anjuros.website.redesign.styles.FormsFieldStyle
import com.anjuros.website.redesign.styles.FormsRequiredStyle
import com.anjuros.website.redesign.styles.FormsSentStyle
import com.anjuros.website.redesign.styles.FormsSpamNoteStyle
import com.anjuros.website.redesign.styles.FormsSubmitCardStyle
import com.anjuros.website.redesign.styles.RdPrimaryButtonStyle
import com.anjuros.website.redesign.util.RdIcons
import com.anjuros.website.redesign.util.RdText
import com.anjuros.website.redesign.util.rdList
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.name
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.B
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Fieldset
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Legend
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextArea

/** One example form plus its demo submit button. Field values come from the parent so the inbox can mirror them. */
@Composable
fun DemoFormView(
    form: DemoForm,
    values: List<String>,
    onChange: (index: Int, value: String) -> Unit,
    sent: Boolean,
    onSubmit: () -> Unit,
) {
    Div {
        Div(FormsCardStyle.toModifier().toAttrs()) {
            Div(FormsCardTitleStyle.toModifier().toAttrs()) {
                B { Text(LocalText(form.title)) }
                Span { Text(LocalText(form.kind)) }
            }
            form.fields.forEachIndexed { index, field ->
                FieldView("${form.id}-$index", field, values[index]) { onChange(index, it) }
            }
        }
        Div(FormsCardStyle.toModifier().then(FormsSubmitCardStyle.toModifier()).toAttrs()) {
            P(FormsSpamNoteStyle.toModifier().toAttrs()) {
                RdIcons.ShieldCheck(size = 20)
                Span {
                    Text(LocalText(RdText.FORMS_SPAM_NOTE))
                    A("#spam") { Text(LocalText(RdText.FORMS_SPAM_LINK)) }
                }
            }
            Button(RdPrimaryButtonStyle.toModifier().onClick { onSubmit() }.toAttrs { attr("type", "button") }) {
                Text(LocalText(RdText.FORMS_DEMO_SUBMIT))
            }
            if (sent) P(FormsSentStyle.toModifier().toAttrs()) { Text(LocalText(RdText.FORMS_DEMO_SENT)) }
        }
    }
}

@Composable
private fun FieldView(id: String, field: DemoField, value: String, onChange: (String) -> Unit) {
    when (field.type) {
        DemoFieldType.RADIO -> ChoiceGroup(id, field, selected = listOf(value), multiple = false) { option, _ -> onChange(option) }
        DemoFieldType.CHECKS -> ChoiceGroup(id, field, selected = rdList(value), multiple = true) { option, checked ->
            val current = rdList(value).filter { it.isNotBlank() }.toMutableList()
            if (checked) current.add(option) else current.remove(option)
            onChange(current.joinToString("|"))
        }
        else -> Label(forId = id, attrs = FormsFieldStyle.toModifier().toAttrs()) {
            FieldLabel(field)
            when (field.type) {
                DemoFieldType.AREA -> TextArea(value) {
                    id(id)
                    onInput { onChange(it.value) }
                }
                DemoFieldType.SELECT -> Select({
                    id(id)
                    onChange { onChange(it.value ?: "") }
                }) {
                    rdList(LocalText(field.options!!)).forEach { option ->
                        Option(option, { if (option == value) selected() }) { Text(option) }
                    }
                }
                else -> Input(field.type.inputType()) {
                    id(id)
                    value(value)
                    onInput { onChange(it.value.toString()) }
                }
            }
        }
    }
}

@Composable
private fun ChoiceGroup(id: String, field: DemoField, selected: List<String>, multiple: Boolean, onToggle: (String, Boolean) -> Unit) {
    Fieldset(FormsChoicesStyle.toModifier().toAttrs()) {
        Legend { FieldLabel(field) }
        Div {
            rdList(LocalText(field.options!!)).forEachIndexed { index, option ->
                val optionId = "$id-$index"
                val checked = option in selected
                Label(forId = optionId, attrs = Modifier.thenIf(checked, FormsChoiceCheckedStyle.toModifier()).toAttrs()) {
                    if (multiple) {
                        Input(InputType.Checkbox) {
                            id(optionId)
                            checked(checked)
                            onChange { onToggle(option, it.value) }
                        }
                    } else {
                        Input(InputType.Radio) {
                            id(optionId)
                            name(id)
                            checked(checked)
                            onChange { onToggle(option, true) }
                        }
                    }
                    Text(option)
                }
            }
        }
    }
}

@Composable
private fun FieldLabel(field: DemoField) {
    // One span, so the text and the required star stay on one line inside the grid label.
    Span {
        Text(LocalText(field.label))
        if (field.required) Span(FormsRequiredStyle.toModifier().toAttrs()) { Text(" *") }
    }
}

private fun DemoFieldType.inputType(): InputType<String> = when (this) {
    DemoFieldType.EMAIL -> InputType.Email
    DemoFieldType.PHONE -> InputType.Tel
    DemoFieldType.DATE -> InputType.Date
    else -> InputType.Text
}
